import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Copy, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "@/components/ui/sonner";

export default function Results() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [publicData, setPublicData] = useState(null);

  useEffect(() => {
    if (user === null) { navigate("/auth"); return; }
    if (user) {
      api.get("/quiz/my-results").then(({ data }) => setData(data)).catch(() => setData({ empty: true }));
    }
    api.get("/public/results").then(({ data }) => setPublicData(data)).catch(() => {});
  }, [user, navigate]);

  const overall = data?.overall;
  const copyCard = () => {
    const text = `I completed the LostDrkc Independence Day Quiz 🇮🇳\nScore: ${overall?.score ?? 0}\nRank: #${overall?.rank ?? "-"}\n— LostDrkc Quiz`;
    navigator.clipboard?.writeText(text);
    toast.success("Result card copied — paste it anywhere!");
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16" data-testid="results-page">
      <div className="text-xs font-bold tracking-[0.3em] text-[#FF9933] uppercase">Results</div>
      <h1 className="text-4xl font-black font-heading tracking-tighter text-slate-900 mt-2">
        {data?.display_name ? <>Congratulations, <span className="text-gradient-tricolor">{data.display_name}</span>!</> : "Results"}
      </h1>

      {data?.completed_all && (
        <div data-testid="quiz-completed-banner" className="mt-6 bg-[#138808]/10 border border-[#138808]/30 text-[#138808] rounded-xl px-5 py-4 font-bold">
          QUIZ COMPLETED 🇮🇳 Thank you for participating in the LostDrkc Independence Day Special Quiz Competition.
        </div>
      )}

      {overall ? (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
          <div className="relative bg-white border border-slate-200 rounded-2xl p-8 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 tricolor-bar" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div><div className="font-timer text-4xl font-bold text-[#FF9933]" data-testid="result-score">{overall.score}</div><div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mt-1">Final Score</div></div>
              <div><div className="font-timer text-4xl font-bold text-[#003366]" data-testid="result-rank">#{overall.rank}</div><div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mt-1">Rank</div></div>
              <div><div className="font-timer text-4xl font-bold text-[#138808]" data-testid="result-correct">{overall.correct}</div><div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mt-1">Correct</div></div>
              <div><div className="font-timer text-4xl font-bold text-slate-700">{(overall.time_ms / 1000).toFixed(0)}s</div><div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mt-1">Total Time</div></div>
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button onClick={copyCard} data-testid="copy-result-card" className="px-5 py-2.5 rounded-lg bg-[#FF9933] text-white text-sm font-bold hover:bg-[#E6892E] transition-colors inline-flex items-center gap-2"><Copy className="w-4 h-4" /> Copy Result Card</button>
              <a data-testid="share-result-whatsapp" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(`I scored ${overall.score} (Rank #${overall.rank}) in the LostDC Independence Day Quiz 🇮🇳`)}`} className="px-5 py-2.5 rounded-lg border-2 border-[#138808] text-[#138808] text-sm font-bold hover:bg-[#138808] hover:text-white transition-colors inline-flex items-center gap-2"><Share2 className="w-4 h-4" /> Share</a>
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {(data.per_category || []).map((c) => (
              <div key={c.category} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center justify-between" data-testid={`result-cat-${c.category.replace(/\s+/g, "-").toLowerCase()}`}>
                <div>
                  <div className="font-heading font-bold text-slate-900">{c.category}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{c.correct}/{c.total} correct • {(c.time_ms / 1000).toFixed(0)}s {c.status !== "finished" && "• in progress"}</div>
                </div>
                <div className="font-timer text-2xl font-bold" style={{ color: c.color }}>{c.score}</div>
              </div>
            ))}
          </div>
        </motion.div>
      ) : (
        <div className="mt-8 bg-slate-50 border border-slate-200 rounded-xl p-10 text-center">
          <Trophy className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading font-bold text-slate-600">No results yet</div>
          <p className="text-sm text-slate-400 mt-1">Complete a category to see your results here.</p>
          <Link to="/dashboard" data-testid="results-dashboard-link" className="mt-5 inline-block px-6 py-3 rounded-lg bg-[#FF9933] text-white text-sm font-bold hover:bg-[#E6892E] transition-colors">Go to Dashboard</Link>
        </div>
      )}

      {publicData?.winners?.length > 0 && (
        <div className="mt-14">
          <h2 className="text-2xl font-black font-heading text-slate-900 tracking-tight">Winners 🇮🇳</h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {publicData.winners.map((w, i) => (
              <motion.div key={`${w.user_id}-${w.award}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className="relative bg-white rounded-xl p-[2px]" style={{ background: "linear-gradient(135deg,#FF9933,#fff,#138808)" }}>
                <div className="bg-white rounded-[10px] p-6 text-center" data-testid={`winner-card-${i}`}>
                  <div className="w-12 h-12 mx-auto rounded-full bg-[#FF9933]/10 flex items-center justify-center mb-3"><Trophy className="w-6 h-6 text-[#FF9933]" /></div>
                  <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">{w.award}</div>
                  <div className="font-heading font-black text-lg text-slate-900 mt-1">{w.display_name}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
